import axiosInstance from '../../api/axios';

// Row types mirror the backend row structs (and their `json` tags) verbatim —
// see skeptics-backend/repository/analytics_reports_repository.go
// (AuditorControlInventoryRow, AuditorWorkpapersPackageRow,
// AuditorExceptionReportRow, AuditorIssueLedgerRow, AuditorRelianceEligibleRow).
// Do not rename fields without updating both sides of the contract.
//
// Every External Auditor report is read-only (no edit actions in the UI) and
// org-scoped server-side the same way as every other persona's reports.

/** Report #37 — Control Inventory for Reliance. */
export type AuditorControlInventoryRow = {
  control_id: string;
  title: string;
  control_type: string | null;
  frequency: string | null;
  significance: string | null;
  mrc_control: boolean;
  status: string | null;
};

/**
 * Report #38 — Testing Workpapers Package, one row per attribute result on
 * the control's current run's latest execution attempt (execution-level
 * fields are repeated per row). Takes `control_id`, not an assignee.
 */
export type AuditorWorkpapersPackageRow = {
  overall_status: string | null;
  conclusion: string | null;
  confidence_score: number | null;
  executive_summary: Record<string, unknown> | null;
  attribute_ref: string | null;
  label: string | null;
  attribute_status: string | null;
  findings_summary: string | null;
  detailed_findings: string | null;
  deviation_rate: number | null;
  preparer_name: string | null;
  reviewer_name: string | null;
  segregation_verified: boolean;
  review_notes: string | null;
};

/** Report #39 — Exception Report (sample-level exceptions, current runs only). */
export type AuditorExceptionReportRow = {
  control_id: string;
  title: string;
  attribute_ref: string | null;
  label: string | null;
  record_ref: string;
  observation: string | null;
  severity: string | null;
  deviation_rate: number | null;
};

/** Report #40 — Issue Ledger. */
export type AuditorIssueLedgerRow = {
  title: string;
  status: string;
  classification: string | null;
  department: string | null;
  period: string | null;
  is_repeat: boolean;
  vintage_count: number;
  summary_of_deficiency: string | null;
  linked_controls: string | null;
  resolved_actions: number;
  total_actions: number;
};

/** Report #41 — Reliance-Eligible Controls (Scoping Aid). */
export type AuditorRelianceEligibleRow = {
  control_id: string;
  title: string;
  control_type: string | null;
  significance: string | null;
  mrc_control: boolean;
  itgc_domain: string | null;
  max_risk_level: string | null;
};

/**
 * Report — Process Charting Status (feedback round 2026-08). "charted" =
 * process has ≥1 current flowchart version.
 */
export type AuditorProcessChartingByCycleRow = { cycle_name: string; total: number; charted: number };
export type AuditorProcessChartingStatusResponse = {
  total_processes: number;
  charted: number;
  uncharted: number;
  by_cycle: AuditorProcessChartingByCycleRow[];
};

type RowsEnvelope<T> = { rows: T[] };

async function getRows<T>(path: string): Promise<T[]> {
  const response = await axiosInstance.get<RowsEnvelope<T>>(path);
  return response?.data?.rows ?? [];
}

const BASE = '/api/analytics/auditor';

export const fetchAuditorControlInventory = (): Promise<AuditorControlInventoryRow[]> =>
  getRows<AuditorControlInventoryRow>(`${BASE}/control-inventory`);

/**
 * Report #38 requires a `control_id` (UUID) query param — the caller must
 * supply one explicitly (e.g. from a control-id input/picker in the UI).
 */
export const fetchAuditorWorkpapersPackage = (controlId: string): Promise<AuditorWorkpapersPackageRow[]> =>
  getRows<AuditorWorkpapersPackageRow>(`${BASE}/workpapers-package?control_id=${encodeURIComponent(controlId)}`);

export const fetchAuditorExceptionReport = (): Promise<AuditorExceptionReportRow[]> =>
  getRows<AuditorExceptionReportRow>(`${BASE}/exception-report`);

export const fetchAuditorIssueLedger = (): Promise<AuditorIssueLedgerRow[]> =>
  getRows<AuditorIssueLedgerRow>(`${BASE}/issue-ledger`);

export const fetchAuditorRelianceEligible = (): Promise<AuditorRelianceEligibleRow[]> =>
  getRows<AuditorRelianceEligibleRow>(`${BASE}/reliance-eligible`);

export const fetchAuditorProcessChartingStatus = async (): Promise<AuditorProcessChartingStatusResponse> => {
  const response = await axiosInstance.get<AuditorProcessChartingStatusResponse>(`${BASE}/process-charting-status`);
  return response?.data ?? { total_processes: 0, charted: 0, uncharted: 0, by_cycle: [] };
};
